import React from 'react'
import { useHistory } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroller'
import BookShelf from 'components/modular/shelves/book-shelf'
import { useSuggestions } from './Suggestions.hooks'
import { getBooksBadges } from 'utils/badges'
import {
  SHOW_SHELF,
  SHOW_ATUALIDADES_SHELF,
  INTERMEDIATE_DETAILS_BOOK_PATH
} from 'routes/paths'
import { post_event } from 'utils/analytics'
import PremiumLock from 'utils/premiumLock'
import SpinnerLoader from 'components/loader'
import ShelfSkeleton from 'components/shelf-skeleton/ShelfSkeleton'
import type { SuggestionsProps } from './Suggestions.types'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useApolloClient } from '@apollo/client'
import type { LanguageTokens } from 'i18n'
import { useTranslation } from 'i18n'
import { Module } from 'api/data.types'
import { shouldUseNextjsBookIntermediate } from 'utils/identify-modules/IdentifyModules'
import {
  applySelection,
  bookShelfKey
} from 'services/news-tab-layout/shelfSelection'

const Suggestions: React.FC<SuggestionsProps> = ({
  contentFilter,
  degree,
  isStudentView = false,
  selection
}) => {
  const client = useApolloClient()
  const history = useHistory()

  const { shelves, getShelves, hasMoreShelves } = useSuggestions(
    client,
    contentFilter,
    isStudentView,
    degree
  )

  const { user } = useUserInfo()
  const { t } = useTranslation()

  const showGetMoreButton = suggestion => {
    post_event(`${suggestion?.name}/ClickVerTudo/${contentFilter}`, 'TRUE')
    if (contentFilter === Module.ATUALIDADES) {
      history.push(SHOW_ATUALIDADES_SHELF(suggestion?.id))
    } else {
      history.push(SHOW_SHELF(suggestion?.id))
    }
  }

  const gotToIntermediaryPage = (slug, premiumLock, suggestion) => {
    if (premiumLock) {
      PremiumLock.sendBookClickEvent({ slug })
    } else {
      if (shouldUseNextjsBookIntermediate()) {
        window.location.href = `/app-v2/book/intermediate/${slug}`
        return
      }
      return history.push(INTERMEDIATE_DETAILS_BOOK_PATH(slug))
    }
    post_event(`${suggestion?.name}/ClickCapaLivro/${contentFilter}`, slug)
  }

  const isLoadingFirstShelves = shelves.length === 0 && hasMoreShelves
  const visibleShelves = applySelection(
    shelves,
    shelf => bookShelfKey(shelf?.name ?? ''),
    selection
  )

  if (selection && !isLoadingFirstShelves && visibleShelves.length === 0) {
    return null
  }

  return (
    <InfiniteScroll
      loadMore={getShelves}
      hasMore={hasMoreShelves}
      loader={
        !isLoadingFirstShelves && <SpinnerLoader key={'spinner-loader'} />
      }
    >
      {isLoadingFirstShelves &&
        Array.from({ length: selection ? 1 : 3 }).map((_, index) => (
          <ShelfSkeleton key={index} itemsCount={5} type="books" />
        ))}
      {visibleShelves.map(suggestion => {
        const hasShelfId = Boolean(suggestion?.id)
        const coversList = suggestion.bookShelfItems?.map(({ book, id }) => {
          const tag = getBooksBadges({ book, user })
          return {
            id,
            title: book?.name,
            slug: book?.slug,
            author: book?.author,
            imageUrlThumb: book?.imageUrlThumb,
            hasAudioBook: book?.hasAudioBook,
            language: book?.language,
            premiumLock: book?.premiumLock,
            degreesRange: tag && [
              { tagName: tag?.name, fullName: tag?.fullName }
            ]
          }
        })
        return (
          <BookShelf
            title={t(suggestion?.name as LanguageTokens)}
            subtitle={
              !user?.featureToggles?.storyTree
                ? suggestion?.subtitle
                : undefined
            }
            contentFilter={contentFilter}
            isSeeAllButtonVisible={hasShelfId}
            format="book"
            size={'large'}
            showInfo
            onClickBtnGetMore={() => showGetMoreButton(suggestion)}
            coversList={coversList}
            onClickCover={({ slug, premiumLock }) =>
              gotToIntermediaryPage(slug, premiumLock, suggestion)
            }
            key={suggestion.id}
          />
        )
      })}
    </InfiniteScroll>
  )
}

export default Suggestions
